// EventContext: A type-safe, generic event bus for React applications.
// Allows components to trigger and subscribe to named events with strongly-typed payloads.
// Usage:
//   - Wrap your app in <EventProvider>.
//   - Use useEventTrigger() to send events.
//   - Use useEventSubscription() to listen for events (auto-unsubscribes on unmount).

import React, { createContext, useContext, useCallback, useRef } from "react";
import {EventDataMap} from "./eventDataMap";

// EventName is a union of all valid event names.
type EventName = keyof EventDataMap;

// EventListener is a function that receives the payload for a given event.
type EventListener<K extends EventName> = (data: EventDataMap[K]) => void;

// EventContextType defines the API for the event bus.
// - trigger: send an event with a payload.
// - subscribe: listen for an event; returns an unsubscribe function.
type EventContextType = {
  trigger: <K extends EventName>(name: K, data: EventDataMap[K]) => void;
  subscribe: <K extends EventName>(name: K, fn: EventListener<K>) => () => void;
};

// Create the React context with default no-op implementations.
const EventContext = createContext<EventContextType>({
  trigger: () => {},
  subscribe: () => () => {},
});

// EventProvider wraps your app and provides the event bus context.
// Internally, it manages a map of event listeners for each event name.
export const EventProvider: React.FC = ({ children }) => {
  // listeners: Map from event name to a set of listener functions.
  // We use 'any' for the function signature internally, but the API is type-safe.
  const listeners = useRef<Map<string, Set<(data: any) => void>>>(new Map());

  // trigger: Notify all listeners for the given event name, passing the payload.
  const trigger = useCallback(<K extends EventName>(name: K, data: EventDataMap[K]) => {
    const set = listeners.current.get(name);
    if (set) {
      set.forEach(fn => fn(data));
    }
  }, []);

  // subscribe: Register a listener for the given event name.
  // Returns an unsubscribe function to remove the listener.
  const subscribe = useCallback(<K extends EventName>(name: K, fn: EventListener<K>) => {
    if (!listeners.current.has(name)) {
      listeners.current.set(name, new Set());
    }
    listeners.current.get(name)!.add(fn as (data: any) => void);
    return () => {
      listeners.current.get(name)!.delete(fn as (data: any) => void);
    };
  }, []);

  return (
    <EventContext.Provider value={{ trigger, subscribe }}>
      {children}
    </EventContext.Provider>
  );
};

// useEventTrigger: Returns the type-safe trigger function for sending events.
export const useEventTrigger = () => {
  const ctx = useContext(EventContext);
  return ctx.trigger;
};

// useEventSubscription: Subscribes to an event and automatically unsubscribes on unmount.
// - name: The event name to subscribe to.
// - fn: The callback to invoke when the event is triggered.
export function useEventSubscription<K extends EventName>(
  name: K,
  fn: EventListener<K>
) {
  const { subscribe } = useContext(EventContext);
  React.useEffect(() => {
    const unsubscribe = subscribe(name, fn);
    return unsubscribe;
  }, [name, fn, subscribe]);
}
